import React, { useState, useEffect, useMemo } from "react";
import { Row, Col } from "reactstrap";
import { connect } from "react-redux";
import LoadingOverlay from "react-loading-overlay";
import Loader from "react-loaders";
import BackButton from "../../../../designUtils/Logo/BackButton.svg";
import FetchAmazonNotificationLogsService from "../../../../Service/DashboardServices/AmazonNotificationsServices/FetchAmazonNotificationLogsService";
import SaveNotificationTypeLookupService from "../../../../Service/DashboardServices/AmazonNotificationsServices/SaveNotificationTypeLookupService";
import CustomModal from "../../../Components/Modals/CustomModal";
import ReactTable from "react-table";
import NotificationDetailsDrawer from "./Components/NotificationDetailsDrawer";
import GridPagination from "../../../Components/Pagination/GridPagination";
import StoreListService from "../../../../Service/DashboardServices/AccountOverviewServices/StoreServices/StoreListService";
import {
    buildNotificationGuide, getClassification, mapApiNotification, getPrimaryId, payloadSnippet,
    severityBadgeClass, statusBadgeClass, entityBadgeClass, isWithinDateFilter
} from "./Components/NotificationMonitoring";


function AmazonNotificationsDashboard(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [notificationData, setNotificationData] = useState([]);
    const [search, setSearch] = useState("");
    const [guide, setGuide] = useState([]);
    const [showHelp, setShowHelp] = useState(false);
    const [selectedStoreName, setSelectedStoreName] = useState(props.storeName || "");
    const [selectedStoreIntegerID, setSelectedStoreIntegerID] = useState(props.storeIntegerID || undefined);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [typeCounts, setTypeCounts] = useState({});
    const [noficationsActions, setNoficationsActions] = useState([]);
    const [selectedItemKey, setSelectedItemKey] = useState(null);
    const [drawerItem, setDrawerItem] = useState(null);
    const [statusOverrides, setStatusOverrides] = useState({});
    const [filterSeverity, setFilterSeverity] = useState("");
    const [filterNeedsAttention, setFilterNeedsAttention] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [payloadItem, setPayloadItem] = useState(null);
    const pageSize = 2000;

    useEffect(() => {
        if (props.token && (!props.adProfiles || props.adProfiles.length === 0)) {
            props.dispatch(StoreListService(props.token));
        }
        if (props.storeName) {
            setSelectedStoreName(props.storeName);
        }
        if (props.storeIntegerID) {
            setSelectedStoreIntegerID(props.storeIntegerID);
        }
        fetchAmazonNotifications(0, search, props.storeIntegerID);
    }, [props.token, props.storeIntegerID, props.storeName]);

    const fetchAmazonNotifications = (
        page = 0,
        searchText = search,
        storeId = selectedStoreIntegerID
    ) => {
        setIsLoading(true);
        FetchAmazonNotificationLogsService(props.token, {
            page: page + 1,
            pageSize,
            search: searchText,
            integerID: storeId
        }).then((response) => {
            if (response && response.status) {
                setNotificationData(
                    response.notificationList || []
                );
                setTotalRecords(
                    response.totalRecords || 0
                );
                setTypeCounts(
                    response.typeCounts || {}
                );
                setNoficationsActions(
                    response.noficationsActions || []
                );
                setGuide(
                    buildNotificationGuide(
                        response.notificationTypeSettings || []
                    )
                );
            }
            setIsLoading(false);
        });
    };

    const handleGuideChange = (index, field, value, notificationType) => {
        const fieldKey = {
            severity: "Severity",
            businessImpact: "Business_Impact",
            needsAttention: "Attention",
        };
        const key = fieldKey[field];
        const itemType = notificationType || (guide[index] && guide[index].type);
        const lookup = (noficationsActions || []).find(
            (a) => a.key === key && a.value === value
        );
        setGuide((prev) =>
            prev.map((row, i) =>
                i === index
                    ? {
                        ...row,
                        [field]: value
                    }
                    : row
            )
        );
        SaveNotificationTypeLookupService(props.token, {
            entity: "Amazon_Notifications",
            key: key,
            value: value,
            notification_type: itemType,
            lookup_values_id: lookup && lookup.id,
        });
    };

    const mappedNotifications = useMemo(() => {
        return (notificationData || []).map(
            (row, index) => {
                const mapped = mapApiNotification(
                    row,
                    index
                );
                if (statusOverrides[mapped.id]) {
                    mapped.status =
                        statusOverrides[mapped.id];
                }
                return mapped;
            }
        );
    }, [
        notificationData,
        statusOverrides
    ]);

    const storeOptions = useMemo(() => {
        const list = [];
        const seen = new Set();

        if (props.adProfiles && Array.isArray(props.adProfiles)) {
            props.adProfiles.forEach((store) => {
                if (store && store.name && !seen.has(store.name)) {
                    seen.add(store.name);
                    list.push({
                        name: store.name,
                        integerID: store.integerID,
                        location: store.location,
                    });
                }
            });
        }

        (mappedNotifications || []).forEach((n) => {
            if (n.store && !seen.has(n.store)) {
                seen.add(n.store);
                list.push({
                    name: n.store,
                    integerID: null,
                    location: "",
                });
            }
        });

        if (props.storeName && !seen.has(props.storeName)) {
            seen.add(props.storeName);
            list.push({
                name: props.storeName,
                integerID: props.storeIntegerID || null,
                location: "",
            });
        }

        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return list;
    }, [
        props.adProfiles,
        mappedNotifications,
        props.storeName,
        props.storeIntegerID
    ]);


    const activeStore = selectedStoreName || "";

    const handleStoreChange = (e) => {
        const newStoreName = e.target.value;
        setSelectedStoreName(newStoreName);
        setSelectedItemKey(null);
        setCurrentPage(0);

        const storeObj = (storeOptions || []).find(
            (s) => s.name === newStoreName
        );
        const storeId = storeObj ? storeObj.integerID : undefined;
        setSelectedStoreIntegerID(storeId);

        fetchAmazonNotifications(0, search, storeId);
    };

    const storeNotifs = useMemo(
        () =>
            mappedNotifications.filter(
                (n) => {
                    if (!activeStore) {
                        return true;
                    }

                    if (!n.store) {
                        return true;
                    }

                    return (
                        String(n.store).trim() ===
                        String(activeStore).trim()
                    );
                }
            ),
        [
            mappedNotifications,
            activeStore
        ]
    );

    const itemSummary = useMemo(() => {

        return storeNotifs.map((n, index) => {

            const classification =
                getClassification(
                    n.type,
                    guide
                );

            const primaryId =
                getPrimaryId(n) ||
                String(n.id || index);

            return {
                rowKey: `${n.id || index}||${index}`,
                notificationId: n.id,
                sourceIndex: index,
                displayId: primaryId,
                entityType: n.identifierType,
                type: n.type || "",
                asin: n.asin || "-",
                sku: n.sku || "-",
                sellerId: n.sellerId || "-",
                eventTimeRaw: n.eventTimeRaw || "",

                total: 1,
                critical:
                    classification.severity ===
                        "CRITICAL"
                        ? 1
                        : 0,
                high:
                    classification.severity ===
                        "HIGH"
                        ? 1
                        : 0,
                medium:
                    classification.severity ===
                        "MEDIUM"
                        ? 1
                        : 0,
                low:
                    classification.severity !==
                        "CRITICAL" &&
                        classification.severity !==
                        "HIGH" &&
                        classification.severity !==
                        "MEDIUM"
                        ? 1
                        : 0
            };
        });

    }, [
        storeNotifs,
        guide
    ]);

    const effectiveItemKey = useMemo(() => {

        if (
            selectedItemKey &&
            itemSummary.some(
                (item) =>
                    item.rowKey ===
                    selectedItemKey
            )
        ) {
            return selectedItemKey;
        }

        return itemSummary[0]
            ? itemSummary[0].rowKey
            : null;

    }, [
        selectedItemKey,
        itemSummary
    ]);

    const uniqueTypes = useMemo(() => {

        const types = [];

        mappedNotifications.forEach((n) => {

            if (
                n.type &&
                types.indexOf(n.type) === -1
            ) {
                types.push(n.type);
            }

        });

        return types;

    }, [
        mappedNotifications
    ]);

    const filteredIssues = useMemo(() => {

        const q = search.toLowerCase();

        const selectedSummaryItem =
            itemSummary.find(
                (item) =>
                    item.rowKey ===
                    effectiveItemKey
            );

        return storeNotifs.filter((n, index) => {

            if (selectedSummaryItem) {
                const rowKey = `${n.id || index}||${index}`;

                if (
                    rowKey !==
                    selectedSummaryItem.rowKey
                ) {
                    return false;
                }
            }

            const classification =
                getClassification(
                    n.type,
                    guide
                );

            if (
                q &&
                !(n.asin || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.type || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.sku || "")
                    .toLowerCase()
                    .includes(q) &&
                !(n.sellerId || "")
                    .toLowerCase()
                    .includes(q)
            ) {
                return false;
            }

            if (
                filterSeverity &&
                classification.severity !==
                filterSeverity
            ) {
                return false;
            }

            if (
                filterNeedsAttention &&
                classification.needsAttention !==
                filterNeedsAttention
            ) {
                return false;
            }

            if (
                filterStatus &&
                n.status !== filterStatus
            ) {
                return false;
            }

            if (
                filterType &&
                n.type !== filterType
            ) {
                return false;
            }

            if (
                !isWithinDateFilter(
                    n.eventTimeRaw,
                    filterDate
                )
            ) {
                return false;
            }

            return true;
        });

    }, [
        storeNotifs,
        itemSummary,
        effectiveItemKey,
        search,
        guide,
        filterSeverity,
        filterNeedsAttention,
        filterStatus,
        filterType,
        filterDate
    ]);

    const kpi = useMemo(() => {

        let criticalCount = 0;
        let highCount = 0;
        let needsAttentionCount = 0;

        Object.entries(typeCounts).forEach(([type, count]) => {
            const c = getClassification(type, guide);
            if (c.severity === "CRITICAL") criticalCount += count;
            if (c.severity === "HIGH") highCount += count;
            if (c.needsAttention === "Yes") needsAttentionCount += count;
        });

        return {
            total: totalRecords,
            criticalCount,
            highCount,
            needsAttentionCount
        };

    }, [
        typeCounts,
        totalRecords,
        guide
    ]);

    const updateIssueStatus = (
        newStatus
    ) => {

        if (!drawerItem) {
            return;
        }

        setStatusOverrides(
            (prev) => ({
                ...prev,
                [drawerItem.id]:
                    newStatus
            })
        );

        setDrawerItem(null);
    };

    const itemSummaryColumns = useMemo(
        () => [
            {
                Header: "Identifier Type",
                accessor: "entityType",
                Cell: (cellInfo) => (
                    <span
                        className={`anm-badge ${entityBadgeClass(
                            cellInfo.value
                        )}`}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "ASIN",
                accessor: "asin",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        <strong>{cellInfo.value}</strong>
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "SKU",
                accessor: "sku",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        <strong>{cellInfo.value}</strong>
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "Seller ID",
                accessor: "sellerId",
                Cell: (cellInfo) =>
                    cellInfo.value !== "-" ? (
                        <span className="anm-seller-id">
                            {cellInfo.value}
                        </span>
                    ) : (
                        <span className="anm-muted">-</span>
                    ),
            },
            {
                Header: "Total Issues",
                accessor: "total",
                width: 100,
                Cell: (cellInfo) => (
                    <strong>{cellInfo.value}</strong>
                ),
            },
            {
                Header: "Critical",
                accessor: "critical",
                width: 90,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-critical">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "High",
                accessor: "high",
                width: 80,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-high">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Medium",
                accessor: "medium",
                width: 90,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-medium">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Low",
                accessor: "low",
                width: 80,
                Cell: (cellInfo) => (
                    <span className="anm-badge anm-badge-low">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Active Types",
                accessor: "type",
                Cell: (cellInfo) => (
                    <span
                        className="anm-types"
                        title={cellInfo.value || ""}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Action",
                accessor: "rowKey",
                width: 100,
                sortable: false,
                Cell: (cellInfo) => (
                    <button
                        type="button"
                        className="anm-action-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItemKey(
                                cellInfo.original.rowKey
                            );
                        }}
                    >
                        Select
                    </button>
                ),
            },
        ],
        []
    );

    const issueColumns = useMemo(
        () => [
            {
                Header: "Target Entity (ASIN / SKU / Seller ID)",
                id: "targetEntity",
                minWidth: 170,
                Cell: (cellInfo) => {
                    const item = cellInfo.original;

                    if (
                        item.sellerId &&
                        String(item.sellerId).trim() !== ""
                    ) {
                        return (
                            <span>
                                <span className="anm-badge anm-badge-critical">
                                    Seller ID
                                </span>
                                <br />
                                <strong className="anm-seller-id">
                                    {item.sellerId}
                                </strong>
                            </span>
                        );
                    }

                    if (item.asin) {
                        return (
                            <span>
                                <strong>{item.asin}</strong>
                                <br />
                                <span className="anm-small-muted">
                                    SKU: {item.sku || "-"}
                                </span>
                            </span>
                        );
                    }

                    return (
                        <span>
                            <span className="anm-no-asin">
                                No ASIN
                            </span>
                            <br />
                            <strong className="anm-sku">
                                SKU: {item.sku || "-"}
                            </strong>
                        </span>
                    );
                },
            },
            {
                Header: "Notification Type",
                accessor: "type",
                minWidth: 180,
                Cell: (cellInfo) => (
                    <span className="anm-notification-type comment-paragraph">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Notification Payload",
                id: "payload",
                minWidth: 180,
                sortable: false,
                Cell: (cellInfo) => (
                    <span>
                        <span className="anm-payload-snippet">
                            {payloadSnippet(
                                cellInfo.original.payload
                            )}
                        </span>
                        <button
                            type="button"
                            className="anm-action-btn"
                            onClick={() => setPayloadItem(cellInfo.original)}
                        >
                            <i className="far fa-eye" /> View Payload
                        </button>
                    </span>
                ),
            },
            {
                Header: "Event Time",
                accessor: "eventTime",
                minWidth: 140,
                Cell: (cellInfo) => (
                    <span className="anm-nowrap">
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Severity",
                id: "severity",
                width: 110,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide
                    );
                    return (
                        <span
                            className={`anm-badge ${severityBadgeClass(
                                classification.severity
                            )}`}
                        >
                            {classification.severity}
                        </span>
                    );
                },
            },
            {
                Header: "Business Impact",
                id: "businessImpact",
                width: 130,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide
                    );
                    return (
                        <span className="anm-business-impact">
                            {classification.businessImpact}
                        </span>
                    );
                },
            },
            {
                Header: "Needs Attention",
                id: "needsAttention",
                width: 130,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide
                    );
                    return (
                        <span
                            className={`anm-badge ${classification.needsAttention ===
                                    "Yes"
                                    ? "anm-badge-attention-yes"
                                    : "anm-badge-attention-no"
                                }`}
                        >
                            {classification.needsAttention}
                        </span>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                width: 120,
                Cell: (cellInfo) => (
                    <span
                        className={`anm-badge ${statusBadgeClass(
                            cellInfo.value
                        )}`}
                    >
                        {cellInfo.value}
                    </span>
                ),
            },
            {
                Header: "Recommended Action",
                id: "recommendedAction",
                minWidth: 180,
                Cell: (cellInfo) => {
                    const classification = getClassification(
                        cellInfo.original.type,
                        guide
                    );
                    return (
                        <span
                            className="anm-recommended-text"
                            title={
                                classification.recommendedAction
                            }
                        >
                            {classification.recommendedAction}
                        </span>
                    );
                },
            },
            {
                Header: "Action",
                id: "viewAction",
                width: 100,
                sortable: false,
                Cell: (cellInfo) => (
                    <button
                        type="button"
                        className="anm-action-btn"
                        onClick={() =>
                            setDrawerItem(cellInfo.original)
                        }
                    >
                        <i className="far fa-eye" /> View
                    </button>
                ),
            },
        ],
        [guide]
    );

    const guideColumns = [
        {
            Header: "Notification Type",
            accessor: "type",
            width: 180,
            Cell: (cellInfo) => (
                <span className="comment-paragraph">
                    {cellInfo.value}
                </span>
            ),
        },
        {
            Header: "Category",
            accessor: "category",
            width: 165
        },
        {
            Header: "Severity",
            accessor: "severity",
            width: 140,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "severity",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    {[...new Set(noficationsActions.filter((a) => a.key === "Severity").map((a) => a.value))].map((v) => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>
            )
        },
        {
            Header: "Business Impact",
            accessor: "businessImpact",
            width: 165,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "businessImpact",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    {[...new Set(noficationsActions.filter((a) => a.key === "Business_Impact").map((a) => a.value))].map((v) => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>
            )
        },
        {
            Header: "Attention",
            accessor: "needsAttention",
            width: 140,
            Cell: (cellInfo) => (
                <select
                    className="form-control form-control-sm"
                    value={cellInfo.value || ""}
                    onChange={(e) =>
                        handleGuideChange(
                            cellInfo.index,
                            "needsAttention",
                            e.target.value,
                            cellInfo.original && cellInfo.original.type
                        )
                    }
                >
                    {[...new Set(noficationsActions.filter((a) => a.key === "Attention").map((a) => a.value))].map((v) => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>
            )
        },
        {
            Header: "Recommended Action",
            accessor: "recommendedAction",
            minWidth: 260
        }
    ];

    return (
        <div className="anm-monitor mt-5">
            <Row className="align-items-center mb-4 mt-2">

                <Col md={7} className="d-flex align-items-center">

                    <button
                        type="button"
                        className="amazon-notification-back-button-header d-flex align-items-center"
                        onClick={props.onBackHeader}
                    >
                        <img
                            src={BackButton}
                            alt="Back"
                        />
                    </button>

                    <span className="amazon-notification-title ml-2">
                        Amazon Notification Monitoring
                    </span>

                </Col>

                <Col
                    md={5}
                    className="d-flex justify-content-end align-items-center"
                >
                    <button
                        type="button"
                        className="bulk-creation-button"
                        onClick={() => setShowHelp(true)}
                    >
                        Amazon Notification Settings
                    </button>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <div className="anm-kpi-card total">
                        <div className="anm-kpi-title">
                            Total Issues (
                            {activeStore || "All Stores"}
                            )
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.total.toLocaleString()}
                        </div>
                    </div>
                </Col>

                <Col md={3}>
                    <div className="anm-kpi-card critical">
                        <div className="anm-kpi-title">
                            Critical
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.criticalCount}
                        </div>
                    </div>
                </Col>


                <Col md={3}>
                    <div className="anm-kpi-card high">
                        <div className="anm-kpi-title">
                            High
                        </div>

                        <div className="anm-kpi-value">
                            {kpi.highCount}
                        </div>
                    </div>
                </Col>


                <Col md={3}>
                    <div className="anm-kpi-card needs-attention">
                        <div className="anm-kpi-title">
                            Needs Attention
                        </div>
                        <div className="anm-kpi-value">
                            {kpi.needsAttentionCount}
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="anm-card">
                <Row className="anm-card-header align-items-center">
                    <Col md={8}>
                        <h3>
                            Select Store
                        </h3>
                    </Col>


                    <Col md={4}>

                        <div className="text-right">

                            <select
                                className="form-control"
                                value={selectedStoreName}
                                onChange={handleStoreChange}
                            >

                                <option value="">-- Select Store --</option>

                                {storeOptions.map(
                                    (s) => (

                                        <option
                                            key={s.integerID ? `${s.integerID}-${s.name}` : s.name}
                                            value={s.name}
                                        >
                                            {s.location ? `${s.name} (${s.location})` : s.name}
                                        </option>

                                    )
                                )}

                            </select>

                        </div>

                    </Col>

                </Row>

            </div>

            <LoadingOverlay
                active={isLoading}
                spinner={
                    <Loader
                        active
                        type="ball-pulse"
                    />
                }
                text="Loading Amazon Notifications..."
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: "#f1f4f6",
                        opacity: 1
                    }),
                    content: (base) => ({
                        ...base,
                        color: "#000"
                    })
                }}
            >

                <div className="anm-card">

                    <Row className="anm-card-header align-items-center">

                        <Col md={8}>

                            <h3>
                                ASIN / SKU / Seller ID-Level
                                Issue Summary
                                {activeStore
                                    ? ` for ${activeStore}`
                                    : ""}
                            </h3>

                        </Col>

                        <Col md={4} className="d-flex justify-content-end align-items-center">

                            <GridPagination
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                currentPageProp={currentPage}
                                onPageChange={({ currentPage: newPage }) => {
                                    setCurrentPage(newPage);
                                    fetchAmazonNotifications(
                                        newPage,
                                        search,
                                        selectedStoreIntegerID
                                    );
                                }}
                            />

                        </Col>

                    </Row>

                    <ReactTable
                        data={itemSummary}
                        columns={itemSummaryColumns}
                        className="-fixed -highlight -striped mt-2"
                        getTheadProps={() => ({
                            style: {
                                overflowY: "hidden",
                                background: "#B1FA63",
                                color: "#243837",
                            },
                        })}
                        getNoDataProps={() => ({
                            style: {
                                display: "none",
                            },
                        })}
                        getTrProps={(state, rowInfo) => {
                            if (!rowInfo) {
                                return {};
                            }
                            const isSelected =
                                rowInfo.original.rowKey ===
                                effectiveItemKey;
                            return {
                                className: isSelected
                                    ? "anm-item-row selected"
                                    : "anm-item-row",
                                onClick: () =>
                                    setSelectedItemKey(
                                        rowInfo.original.rowKey
                                    ),
                            };
                        }}
                        defaultPageSize={12}
                        minRows={0}
                        noDataText=""
                    />

                </div>

                <div className="anm-card">

                    <Row className="anm-card-header align-items-center">

                        <Col md={8}>

                            <h3>
                                Notification Issues
                            </h3>

                        </Col>

                    </Row>

                    <div className="anm-filter-toolbar">

                        <div className="anm-filter-row">

                            <input
                                type="text"
                                className="form-control w-25"
                                placeholder="Search ASIN, SKU, Seller ID, Notification type..."
                                value={search}
                                onChange={(e) => {

                                    const value =
                                        e.target.value;

                                    setSearch(value);
                                    setCurrentPage(0);

                                    fetchAmazonNotifications(
                                        0,
                                        value,
                                        selectedStoreIntegerID
                                    );

                                }}
                            />

                            <select
                                className="form-control"
                                value={
                                    filterSeverity
                                }
                                onChange={(e) =>
                                    setFilterSeverity(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterSeverity
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Severity --
                                </option>

                                <option value="CRITICAL">
                                    Critical
                                </option>

                                <option value="HIGH">
                                    High
                                </option>

                                <option value="MEDIUM">
                                    Medium
                                </option>

                                <option value="LOW">
                                    Low
                                </option>

                            </select>

                            <select
                                className="form-control"
                                value={
                                    filterNeedsAttention
                                }
                                onChange={(e) =>
                                    setFilterNeedsAttention(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterNeedsAttention
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Needs Attention --
                                </option>

                                <option value="Yes">
                                    Yes
                                </option>

                                <option value="No">
                                    No
                                </option>

                            </select>

                            <select
                                className="form-control"
                                value={filterStatus}
                                onChange={(e) =>
                                    setFilterStatus(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterStatus
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Status --
                                </option>

                                <option value="NEW">
                                    New
                                </option>

                                <option value="OPEN">
                                    Open
                                </option>

                                <option value="IN PROGRESS">
                                    In Progress
                                </option>

                                <option value="RESOLVED">
                                    Resolved
                                </option>

                                <option value="INFORMATIONAL">
                                    Informational
                                </option>

                            </select>

                            <select
                                className="form-control"
                                value={filterDate}
                                onChange={(e) =>
                                    setFilterDate(
                                        e.target.value
                                    )
                                }
                                style={{
                                    color: !filterDate
                                        ? "#6c757d"
                                        : "#050505",
                                }}
                            >

                                <option value="">
                                    -- Select Date --
                                </option>

                                <option value="today">
                                    Today
                                </option>

                                <option value="7days">
                                    Last 7 Days
                                </option>
                            </select>
                            <select
                                className="form-control"
                                value={filterType}
                                onChange={(e) =>
                                    setFilterType(
                                        e.target.value
                                    )
                                }
                                style={{ color: !filterType ? "#6c757d" : "#050505", }}>
                                <option value="">-- Notification Types --</option>
                                {uniqueTypes.map(
                                    (type) => (
                                        <option
                                            key={type}
                                            value={type}
                                        >
                                            {type}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                    <ReactTable
                        data={filteredIssues}
                        columns={issueColumns}
                        className="-fixed -highlight -striped mt-2"
                        getTheadProps={() => ({
                            style: {
                                overflowY: "hidden",
                                background: "#B1FA63",
                                color: "#243837",
                            },
                        })}
                        getNoDataProps={() => ({
                            style: {
                                display: "none",
                            },
                        })}
                        defaultPageSize={10}
                        minRows={0}
                        noDataText=""
                    />
                </div>
            </LoadingOverlay>

            {drawerItem && (
                <CustomModal
                    header="Notification Details"
                    isOpen={!!drawerItem}
                    toggle={() => setDrawerItem(null)}
                    size="xl"
                    className="curve-popup"
                >
                    <NotificationDetailsDrawer
                        item={drawerItem}
                        guide={guide}
                        onClose={() =>
                            setDrawerItem(null)
                        }
                        onUpdateStatus={
                            updateIssueStatus
                        }
                    />
                </CustomModal>
            )}
            {payloadItem && (
                <CustomModal
                    header="Notification Payload"
                    isOpen={!!payloadItem}
                    toggle={() => setPayloadItem(null)}
                    size="xl"
                    className="curve-popup"
                >
                    <NotificationDetailsDrawer
                        item={payloadItem}
                        onlyPayload={true}
                    />
                </CustomModal>
            )}
            <CustomModal
                header="Amazon Notification Settings"
                isOpen={showHelp}
                toggle={() => setShowHelp(false)}
                size="xl"
                className="curve-popup"
            >
                <Row>
                    <Col md={12}>
                        <ReactTable
                            data={guide || []}
                            columns={guideColumns}
                            className="-fixed -highlight -striped mt-2"
                            getTheadProps={() => ({
                                style: {
                                    overflowY: "hidden",
                                    background: "#B1FA63",
                                    color: "#243837",
                                },
                            })}
                            defaultPageSize={10}
                            noDataText="No Data Found."
                        />
                    </Col>
                </Row>
            </CustomModal>
        </div>
    );
}

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
    adProfiles: state.accountOverview.landing.adProfiles
});
export default connect(mapStateToProps)(AmazonNotificationsDashboard);